import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useState } from 'react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { Trans, Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
  ArrowLeftRight,
  Ellipsis,
  List,
  Mail,
  MapPin,
  PencilLine,
  Phone,
  Plus,
  X,
} from 'lucide-react';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import {
  MAX_NOMINATION_COUNT,
  outstandingNominationSlots,
  resolveMaxNominations,
  resolveRequiredNominations,
} from '@duke-hq/svc-api/lib/workPlacement/nominationSlots';
import { PrePlacementNominationReviewStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/PrePlacementNominationReviewStatus.schema';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import { isValidAustralianState } from '@duke-hq/google-address/services/validation/australianStates';
import type { EmployerContactLogWritePayload } from '@duke-hq/svc-api/admin/trpc/routers/workPlacements/employers/schemas';
import { AddEmployerModal } from '../AddEmployerModal';
import type { BusinessDetails, EmployerAddress } from '../AddEmployerModal/types';
import { LogContactForm } from '../components/LogContactForm';
import {
  RecentContactsTimeline,
  logToFormValues,
  removedContactFromLog,
  type ContactLog,
} from '../components/RecentContactsTimeline';
import { contactStatusMeta } from '../components/contactStatusMeta';
import { ChangeContactStatusModal } from './ChangeContactStatusModal';
import { RejectNominationModal } from './RejectNominationModal';
import { testIds } from './testIds';

type Nomination = RouterOutputs['workPlacements']['placements']['listNominations'][number];

function ReviewStatusBadge({
  status,
  isReadOnly = false,
}: {
  status: Nomination['reviewStatus'];
  isReadOnly?: boolean;
}): React.ReactElement {
  const isAccepted = status === 'ACCEPTED';
  const isPending = status === 'PENDING_REVIEW';
  const isRejected = status === 'REJECTED';

  // On a terminal (read-only) placement a still-pending nomination can never be
  // reviewed, so render it neutrally rather than the actionable blue.
  const showPendingAsNeutral = isPending && isReadOnly;

  const colorScheme = isAccepted
    ? 'green'
    : showPendingAsNeutral
      ? 'gray'
      : isPending
        ? 'blue'
        : isRejected
          ? 'red'
          : 'gray';
  const bg = isAccepted
    ? 'bg.success-subtle'
    : showPendingAsNeutral
      ? 'bg.subtle'
      : isPending
        ? 'bg.info-subtle'
        : isRejected
          ? 'bg.danger-subtle'
          : undefined;

  return (
    <Badge
      data-testid={testIds.nominationReviewStatus}
      variant="outline"
      colorScheme={colorScheme}
      bg={bg}
      borderRadius="full"
      px={2}
      textTransform="none"
      fontWeight="medium"
    >
      {isAccepted ? (
        <Trans>Accepted</Trans>
      ) : isPending ? (
        <Trans>Pending review</Trans>
      ) : (
        <Trans>Rejected</Trans>
      )}
    </Badge>
  );
}

function EditedReReviewBadge(): React.ReactElement {
  return (
    <Badge
      variant="outline"
      colorScheme="orange"
      bg="bg.warning-subtle"
      borderRadius="full"
      px={2}
      textTransform="none"
      fontWeight="medium"
    >
      <HStack gap={1}>
        <Icon as={PencilLine} boxSize={iconSize.sm} />
        <Trans>Edited — re-review</Trans>
      </HStack>
    </Badge>
  );
}

// Per-nomination actions, available regardless of review status. Save to
// directory flips to a disabled "In directory" once the employer is listed;
// Log contact works whether or not the employer is in the directory yet.
function TrackedEmployerMenu({
  isInDirectory,
  onChangeStatus,
  onSaveToDirectory,
  onLogContact,
}: {
  isInDirectory: boolean;
  onChangeStatus: () => void;
  onSaveToDirectory: () => void;
  onLogContact: () => void;
}): React.ReactElement {
  return (
    <Menu>
      <MenuButton
        as={IconButton}
        data-testid={testIds.trackedEmployerMenuButton}
        aria-label={t`Tracked employer actions`}
        icon={<Icon as={Ellipsis} boxSize={iconSize.md} />}
        variant="ghost"
        size="sm"
      />
      <MenuList>
        <MenuItem
          data-testid={testIds.changeContactStatusMenuItem}
          icon={<ArrowLeftRight size={iconSize.md} />}
          onClick={onChangeStatus}
        >
          <Trans>Change status</Trans>
        </MenuItem>
        <MenuItem
          data-testid={testIds.saveToDirectoryMenuItem}
          icon={<List size={iconSize.md} />}
          isDisabled={isInDirectory}
          onClick={onSaveToDirectory}
        >
          {isInDirectory ? <Trans>In directory</Trans> : <Trans>Save to directory</Trans>}
        </MenuItem>
        <MenuItem
          data-testid={testIds.logContactMenuItem}
          icon={<Phone size={iconSize.md} />}
          onClick={onLogContact}
        >
          <Trans>Log contact</Trans>
        </MenuItem>
      </MenuList>
    </Menu>
  );
}

function nominationToBusinessDetails(nomination: Nomination): BusinessDetails {
  const [firstName = '', ...rest] = (nomination.contactName ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return {
    name: nomination.businessName,
    abn: '',
    businessType: '',
    contact: {
      firstName,
      lastName: rest.join(' '),
      email: nomination.email ?? '',
      phone: nomination.phone ?? '',
      preferredContactMethod: '',
    },
  };
}

type NominationAddressFields = Pick<
  Nomination,
  | 'formattedAddress'
  | 'addressStreetSubpremise'
  | 'addressStreetNumber'
  | 'addressStreetName'
  | 'addressLocality'
  | 'addressState'
  | 'addressPostcode'
>;

export function nominationToAddress(nomination: NominationAddressFields): EmployerAddress | null {
  if (nomination.addressStreetName == null && nomination.addressLocality == null) {
    return null;
  }
  return {
    formattedAddress: nomination.formattedAddress ?? undefined,
    addressStreetSubpremise: nomination.addressStreetSubpremise ?? undefined,
    addressStreetNumber: nomination.addressStreetNumber ?? undefined,
    addressStreetName: nomination.addressStreetName ?? undefined,
    addressLocality: nomination.addressLocality ?? undefined,
    // The nomination stores state as a plain string; the form wants the States enum.
    addressState: isValidAustralianState(nomination.addressState)
      ? nomination.addressState
      : undefined,
    addressPostcode: nomination.addressPostcode ?? undefined,
  };
}

function NominationRow({
  nomination,
  canReview,
  isReadOnly,
  onSuccess,
  onSaveToDirectory,
}: {
  nomination: Nomination;
  canReview: boolean;
  isReadOnly: boolean;
  onSuccess: () => void;
  onSaveToDirectory: () => void;
}): React.ReactElement {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isRejected = nomination.reviewStatus === 'REJECTED';
  const isPending = nomination.reviewStatus === 'PENDING_REVIEW';
  const isEdited = nomination.editedSinceReviewAt != null;
  const hasBusinessData = nomination.businessName !== '';

  const hasContactName = nomination.contactName != null && nomination.contactName !== '';
  const displayAddress = nomination.formattedAddress ?? nomination.businessAddress;
  const hasAddress = displayAddress != null && displayAddress !== '';

  const acceptMutation = trpcAdmin.workPlacements.placements.reviewNomination.useMutation({
    onSuccess: () => {
      toast({
        status: 'success',
        title: isEdited ? t`Nomination re-confirmed` : t`Nomination accepted`,
      });
      onSuccess();
    },
    onError: (error) => {
      toast({
        status: 'error',
        title: t`Failed to accept nomination`,
        description: error.message,
      });
    },
  });

  const utils = trpcAdmin.useUtils();
  const logModal = useDisclosure();
  const contactStatusModal = useDisclosure();
  const [editingLog, setEditingLog] = useState<ContactLog | null>(null);
  const { employerId } = nomination;

  const { data: employerDetail } = trpcAdmin.workPlacements.employers.getEmployerDetail.useQuery(
    { employerId: employerId ?? '' },
    { enabled: logModal.isOpen && employerId != null },
  );

  const closeLogModal = (): void => {
    logModal.onClose();
    setEditingLog(null);
  };

  const onLogWritten = (title: string, logEmployerId: string): void => {
    toast({ status: 'success', title });
    void utils.workPlacements.placements.listNominations.invalidate();
    void utils.workPlacements.employers.listEmployerContactLogs.invalidate({
      employerId: logEmployerId,
    });
    closeLogModal();
  };

  const onLogError = (message: string): void => {
    toast({ status: 'error', title: t`Failed to log contact`, description: message });
  };

  const createForEmployer = trpcAdmin.workPlacements.employers.createEmployerContactLog.useMutation(
    {
      onSuccess: (log) => onLogWritten(t`Contact logged`, log.employerId),
      onError: (error) => onLogError(error.message),
    },
  );
  const createForNomination =
    trpcAdmin.workPlacements.employers.createNominationContactLog.useMutation({
      onSuccess: (log) => onLogWritten(t`Contact logged`, log.employerId),
      onError: (error) => onLogError(error.message),
    });
  const updateLog = trpcAdmin.workPlacements.employers.updateEmployerContactLog.useMutation({
    onSuccess: (log) => onLogWritten(t`Contact updated`, log.employerId),
    onError: (error) => onLogError(error.message),
  });

  const openLogContact = (): void => {
    setEditingLog(null);
    logModal.onOpen();
  };
  const openEditLog = (log: ContactLog): void => {
    setEditingLog(log);
    logModal.onOpen();
  };

  const handleLogSave = (payload: EmployerContactLogWritePayload): void => {
    if (editingLog != null) {
      updateLog.mutate({ logId: editingLog.id, nominationId: nomination.id, ...payload });
      return;
    }
    if (employerId != null) {
      createForEmployer.mutate({ employerId, nominationId: nomination.id, ...payload });
      return;
    }
    createForNomination.mutate({ nominationId: nomination.id, ...payload });
  };

  const isSavingLog =
    createForEmployer.isPending || createForNomination.isPending || updateLog.isPending;

  return (
    <Box
      data-testid={testIds.nominationItem}
      py={3}
      px={4}
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="2xl"
      bg="bg.canvas"
      opacity={isReadOnly ? 0.6 : 1}
    >
      <Stack gap={2}>
        <HStack justify="space-between" align="center" gap={2}>
          <Text
            flex="1"
            minW={0}
            fontSize="md"
            fontWeight="semibold"
            color={isRejected ? 'fg.muted' : undefined}
            textDecoration={isRejected ? 'line-through' : undefined}
          >
            {nomination.businessName}
          </Text>
          <HStack gap={2} flexShrink={0}>
            <Badge
              data-testid={testIds.contactStatusBadge}
              variant="outline"
              colorScheme="purple"
              borderRadius="full"
              px={2}
              textTransform="none"
              fontWeight="medium"
            >
              {contactStatusMeta[nomination.contactStatus].label()}
            </Badge>
            {canReview && (isPending || isEdited) ? (
              <>
                {isEdited ? <EditedReReviewBadge /> : null}
                <Button
                  data-testid={testIds.nominationAcceptButton}
                  size="sm"
                  colorScheme="green"
                  variant="outline"
                  isLoading={acceptMutation.isPending}
                  onClick={() =>
                    acceptMutation.mutate({
                      nominationId: nomination.id,
                      action: PrePlacementNominationReviewStatusSchema.enum.ACCEPTED,
                    })
                  }
                >
                  {isEdited ? <Trans>Re-confirm</Trans> : <Trans>Accept</Trans>}
                </Button>
                <Button
                  data-testid={testIds.nominationRejectButton}
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  onClick={onOpen}
                >
                  <Trans>Reject</Trans>
                </Button>
                <RejectNominationModal
                  isOpen={isOpen}
                  onClose={onClose}
                  nominationId={nomination.id}
                  onSuccess={() => {
                    onClose();
                    onSuccess();
                  }}
                />
              </>
            ) : (
              <ReviewStatusBadge status={nomination.reviewStatus} isReadOnly={isReadOnly} />
            )}
            <TrackedEmployerMenu
              isInDirectory={nomination.employer?.isInDirectory === true}
              onChangeStatus={contactStatusModal.onOpen}
              onSaveToDirectory={onSaveToDirectory}
              onLogContact={openLogContact}
            />
          </HStack>
        </HStack>
        {isRejected && nomination.reviewNotes != null ? (
          <Box bg="bg.danger-subtle" borderRadius="md" px={3} py={2}>
            <Text fontSize="xs" color="fg.danger">
              <Text as="span" fontWeight="semibold">
                <Trans>Rejection note:</Trans>
              </Text>{' '}
              {nomination.reviewNotes}
            </Text>
          </Box>
        ) : null}
        {isEdited && nomination.editSummary != null ? (
          <Box bg="bg.warning-subtle" borderRadius="md" px={3} py={2}>
            <Text fontSize="xs" color="fg.warning">
              <Text as="span" fontWeight="semibold">
                <Trans>What changed:</Trans>
              </Text>{' '}
              {nomination.editSummary}
            </Text>
          </Box>
        ) : null}
        {hasBusinessData ? (
          <Stack gap={1}>
            <HStack gap={3} fontSize="sm" color="fg.muted" flexWrap="wrap">
              {hasContactName ? <Text>{nomination.contactName}</Text> : null}
              {nomination.phone ? (
                <HStack gap={1}>
                  <Icon as={Phone} boxSize={iconSize.sm} />
                  <Text>{nomination.phone}</Text>
                </HStack>
              ) : null}
              {nomination.email ? (
                <HStack gap={1}>
                  <Icon as={Mail} boxSize={iconSize.sm} />
                  <Text>{nomination.email}</Text>
                </HStack>
              ) : null}
            </HStack>
            {hasAddress ? (
              <HStack gap={1} fontSize="sm" color="fg.muted">
                <Icon as={MapPin} boxSize={iconSize.sm} />
                <Text>{displayAddress}</Text>
              </HStack>
            ) : null}
          </Stack>
        ) : null}
        {!isRejected && (nomination.employerId != null || nomination.studentHasContacted) ? (
          <RecentContactsTimeline
            employerId={nomination.employerId}
            studentHasContacted={nomination.studentHasContacted}
            studentContactedAt={nomination.studentContactedAt}
            onEditLog={openEditLog}
          />
        ) : null}

        <FormModalWrapper
          isOpen={logModal.isOpen}
          onClose={closeLogModal}
          modalContentHeight="auto"
          modalContent={
            <LogContactForm
              key={editingLog?.id ?? 'new'}
              contacts={employerDetail?.contacts ?? []}
              removedContact={editingLog != null ? removedContactFromLog(editingLog) : null}
              initialValues={editingLog != null ? logToFormValues(editingLog) : undefined}
              isEditing={editingLog != null}
              onSave={handleLogSave}
              onCancel={closeLogModal}
              isSaving={isSavingLog}
            />
          }
        />

        <ChangeContactStatusModal
          isOpen={contactStatusModal.isOpen}
          onClose={contactStatusModal.onClose}
          nominationId={nomination.id}
          currentContactStatus={nomination.contactStatus}
          onSuccess={onSuccess}
        />
      </Stack>
    </Box>
  );
}

function SectionTitle(): React.ReactElement {
  return (
    <Text fontSize="lg" fontWeight="semibold">
      <Trans>Reverse marketing</Trans>
    </Text>
  );
}

function AwaitingNominationRow({
  isRequested,
  canCancel,
  isCancelling,
  isCancelDisabled,
  onCancel,
}: {
  isRequested: boolean;
  canCancel: boolean;
  isCancelling: boolean;
  isCancelDisabled: boolean;
  onCancel: () => void;
}): React.ReactElement {
  return (
    <Box
      data-testid={testIds.awaitingNominationItem}
      py={3}
      px={4}
      borderWidth="1px"
      borderStyle="dashed"
      borderColor="border.default"
      borderRadius="2xl"
      bg="bg.canvas"
    >
      <HStack justify="space-between" align="center" gap={2}>
        <Text fontSize="md" color="fg.muted" fontStyle="italic">
          {isRequested ? (
            <Trans>Awaiting another nomination from the student</Trans>
          ) : (
            <Trans>Awaiting nomination from the student</Trans>
          )}
        </Text>
        {isRequested ? (
          <HStack gap={2} flexShrink={0}>
            <Badge
              variant="outline"
              colorScheme="orange"
              bg="bg.warning-subtle"
              borderRadius="full"
              px={2}
              textTransform="none"
              fontWeight="medium"
            >
              <Trans>Requested</Trans>
            </Badge>
            {canCancel ? (
              <IconButton
                data-testid={testIds.cancelNominationRequestButton}
                aria-label={t`Cancel request`}
                icon={<Icon as={X} boxSize={iconSize.sm} />}
                size="xs"
                variant="ghost"
                isLoading={isCancelling}
                isDisabled={isCancelDisabled}
                onClick={onCancel}
              />
            ) : null}
          </HStack>
        ) : null}
      </HStack>
    </Box>
  );
}

function Nominations({
  journeyId,
  courseEnrolmentId,
  canEdit,
  nominationsSubmitted,
  maxNominations,
  journeyPlaced,
}: {
  journeyId: string;
  courseEnrolmentId: number;
  canEdit: boolean;
  nominationsSubmitted: boolean;
  maxNominations: number | null;
  journeyPlaced: boolean;
}): React.ReactElement {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const requestConfirm = useDisclosure();
  // The accepted nomination currently being promoted via "Save to directory"; the
  // Add-Employer modal opens pre-filled from it.
  const [directoryNomination, setDirectoryNomination] = useState<Nomination | null>(null);
  const { data, isLoading, refetch } = trpcAdmin.workPlacements.placements.listNominations.useQuery(
    { journeyId },
  );

  const refreshAfterReview = (): void => {
    void refetch();
    void utils.workPlacements.placements.journeyByEnrolment.invalidate({ courseEnrolmentId });
  };

  const effectiveMax = resolveMaxNominations(maxNominations);

  const setNominationLimit = trpcAdmin.workPlacements.placements.setNominationLimit.useMutation({
    onSuccess: () => {
      toast({ status: 'success', title: t`Another nomination requested` });
      requestConfirm.onClose();
      void utils.workPlacements.placements.journeyByEnrolment.invalidate({ courseEnrolmentId });
    },
    onError: (error) => {
      toast({
        status: 'error',
        title: t`Failed to request another nomination`,
        description: error.message,
      });
      void utils.workPlacements.placements.journeyByEnrolment.invalidate({ courseEnrolmentId });
    },
  });

  const cancelRequest = trpcAdmin.workPlacements.placements.setNominationLimit.useMutation({
    onSuccess: () => {
      toast({ status: 'success', title: t`Nomination request cancelled` });
      void utils.workPlacements.placements.journeyByEnrolment.invalidate({ courseEnrolmentId });
    },
    onError: (error) => {
      toast({
        status: 'error',
        title: t`Failed to cancel nomination request`,
        description: error.message,
      });
      void utils.workPlacements.placements.journeyByEnrolment.invalidate({ courseEnrolmentId });
    },
  });

  const nominations = data ?? [];
  const rank = (nomination: Nomination): number => {
    if (nomination.editedSinceReviewAt != null) {
      return 0;
    }
    if (nomination.reviewStatus === 'PENDING_REVIEW') {
      return 1;
    }
    return nomination.reviewStatus === 'REJECTED' ? 3 : 2;
  };
  const orderedNominations = [...nominations].sort((a, b) => rank(a) - rank(b));
  const editedNominations = nominations.filter((n) => n.editedSinceReviewAt != null);

  // Required nominations still outstanding. A rejected nomination counts toward
  // the base requirement (so it doesn't reopen a slot on its own), but each
  // admin-requested extra always opens a fresh slot - shared with the student
  // views via outstandingNominationSlots. Shown on an active placement (so the
  // default slots are visible before the student has submitted) or once they've
  // submitted on a now read-only placement.
  const requiredTarget = resolveRequiredNominations(maxNominations);
  const activeCount = nominations.filter((n) => n.reviewStatus !== 'REJECTED').length;
  const rejectedCount = nominations.filter((n) => n.reviewStatus === 'REJECTED').length;
  const awaitingCount =
    canEdit || nominationsSubmitted
      ? outstandingNominationSlots(activeCount, rejectedCount, requiredTarget)
      : 0;
  // Of the outstanding slots, the extras opened above the default are the
  // "requested" ones (badged and cancellable); the rest are the base required
  // slots awaiting the student.
  const requestedExtras = Math.max(0, effectiveMax - MAX_NOMINATION_COUNT);
  const requestedAwaiting = Math.min(requestedExtras, awaitingCount);
  const defaultAwaiting = awaitingCount - requestedAwaiting;
  // Anything raised above the default can be cancelled, including a slot opened
  // for a replacement.
  const canCancelRequest = canEdit && effectiveMax > MAX_NOMINATION_COUNT;

  return (
    <>
      <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
        <HStack gap={2} align="baseline">
          <SectionTitle />
          {nominations.length > 0 ? (
            <Text fontSize="sm" color="fg.muted">
              <Plural value={nominations.length} one="# business" other="# businesses" />
            </Text>
          ) : null}
        </HStack>
        {canEdit ? (
          <Button
            data-testid={testIds.requestAnotherNominationButton}
            size="sm"
            variant="ghost"
            leftIcon={<Icon as={Plus} boxSize={iconSize.sm} />}
            isLoading={setNominationLimit.isPending}
            // Both actions send the cap they started from, so a cancel that hasn't
            // settled yet would make a concurrent request stale and be refused.
            isDisabled={cancelRequest.isPending || journeyPlaced}
            onClick={requestConfirm.onOpen}
          >
            <Trans>Request another nomination</Trans>
          </Button>
        ) : null}
      </Flex>

      <ConfirmationDialog
        isOpen={requestConfirm.isOpen}
        onClose={requestConfirm.onClose}
        title={<Trans>Request another nomination?</Trans>}
        body={
          <Trans>
            This opens another placement preference slot for the student to fill and emails them to
            let them know. Are you sure you want to continue?
          </Trans>
        }
        confirmLabel={<Trans>Request another nomination</Trans>}
        isLoading={setNominationLimit.isPending}
        onConfirm={() =>
          setNominationLimit.mutate({
            journeyId,
            fromMaxNominations: effectiveMax,
            maxNominations: effectiveMax + 1,
          })
        }
      />

      {isLoading ? (
        <Center p={4}>
          <Spinner size="md" />
        </Center>
      ) : nominations.length === 0 && awaitingCount === 0 ? (
        <Text fontSize="sm" color="fg.muted">
          <Trans>No nominations submitted yet.</Trans>
        </Text>
      ) : (
        <Stack gap={2}>
          {editedNominations.length > 0 ? (
            <Box bg="bg.warning-subtle" borderRadius="lg" px={4} py={3}>
              <HStack gap={2} align="start">
                <Icon
                  as={PencilLine}
                  boxSize={iconSize.sm}
                  color="fg.warning"
                  mt={pxToRem(2)}
                  flexShrink={0}
                />
                <Text fontSize="sm" color="fg.warning">
                  {editedNominations.length === 1 ? (
                    <Trans>
                      {editedNominations[0]?.businessName} was edited after review. Re-confirm it
                      before you keep contacting the employer.
                    </Trans>
                  ) : (
                    <Trans>
                      {editedNominations.length} nominations were edited after review. Re-confirm
                      them before you keep contacting the employers.
                    </Trans>
                  )}
                </Text>
              </HStack>
            </Box>
          ) : null}
          {orderedNominations.map((nomination) => (
            <NominationRow
              key={nomination.id}
              nomination={nomination}
              canReview={canEdit && nominationsSubmitted}
              isReadOnly={!canEdit}
              onSuccess={refreshAfterReview}
              onSaveToDirectory={() => setDirectoryNomination(nomination)}
            />
          ))}
          {Array.from({ length: defaultAwaiting }, (_, index) => (
            <AwaitingNominationRow
              key={`awaiting-default-${index}`}
              isRequested={false}
              canCancel={false}
              isCancelling={false}
              isCancelDisabled={false}
              onCancel={() => undefined}
            />
          ))}
          {Array.from({ length: requestedAwaiting }, (_, index) => (
            <AwaitingNominationRow
              key={`awaiting-requested-${index}`}
              isRequested
              canCancel={canCancelRequest}
              isCancelling={cancelRequest.isPending}
              isCancelDisabled={setNominationLimit.isPending}
              onCancel={() =>
                cancelRequest.mutate({
                  journeyId,
                  fromMaxNominations: effectiveMax,
                  maxNominations: effectiveMax - 1,
                })
              }
            />
          ))}
        </Stack>
      )}

      {directoryNomination != null ? (
        <AddEmployerModal
          isOpen
          onClose={() => setDirectoryNomination(null)}
          initialBusiness={nominationToBusinessDetails(directoryNomination)}
          initialAddress={nominationToAddress(directoryNomination)}
          nominationId={directoryNomination.id}
        />
      ) : null}
    </>
  );
}

export function ReverseMarketingSection({
  journeyId,
  enrolmentWithdrawn,
  courseEnrolmentId,
  canEdit,
  nominationsSubmitted,
  maxNominations,
  journeyPlaced = false,
}: {
  journeyId: string | null;
  enrolmentWithdrawn: boolean;
  courseEnrolmentId: number;
  canEdit: boolean;
  nominationsSubmitted: boolean;
  maxNominations: number | null;
  journeyPlaced?: boolean;
}): React.ReactElement {
  return (
    <Stack
      bg="bg.surface"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="2xl"
      p={5}
      gap={4}
      data-testid={testIds.nominationsSection}
    >
      {journeyId == null || enrolmentWithdrawn ? (
        <>
          <SectionTitle />
          <Text fontSize="sm" color="fg.muted">
            {enrolmentWithdrawn ? (
              <Trans>This enrolment has been withdrawn.</Trans>
            ) : (
              <Trans>No placement for this student yet.</Trans>
            )}
          </Text>
        </>
      ) : (
        <Nominations
          journeyId={journeyId}
          courseEnrolmentId={courseEnrolmentId}
          canEdit={canEdit}
          nominationsSubmitted={nominationsSubmitted}
          maxNominations={maxNominations}
          journeyPlaced={journeyPlaced}
        />
      )}
    </Stack>
  );
}
